import React, { FC, useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';
import {
  Button,
  PlusIcon,
  Asset,
  Modal,
  Pill,
  WarningIcon,
} from '@clearcover/atlas';
import { LoadingPage, useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api/makeQuoteApiHttp';
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
} from 'QuoteApp/components/Accordion';
import {
  DriverFieldsType,
  DriverType,
  FormValuesType,
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  CreateDriverResponseType,
  transformCreateDriverResponse,
} from 'QuoteApp/services/endpoints/PostAddDriver';
import { useAppContext } from 'QuoteApp/services/providers';
import { formatDriverDisplay, getErrorMessage } from 'QuoteApp/utils';
import { useEdit } from 'QuoteApp/utils/contexts';
import { DriverRemovalModal } from 'QuoteApp/components/DriverRemovalModal';

import { DriverForm } from './DriverForm/DriverForm';
import { SaveDiscardModal } from '../SaveDiscardModal';
import { DriverStatusModal } from './DriverStatusModal/DriverStatusModal';
import { useIsDirtyExcluding } from 'Shared/hooks/useDirtyPaths';
import styles from './styles.module.css';

export type DriverDataType = Record<string, DriverFieldsType>;

export type DriversListPropsType = {
  onDriversPage?: boolean;
  dataCompleteness: Record<string, boolean>;
  editingIdDisplay: string;
};

export const DRIVER_ORDER: Array<DriverType> = [
  'principalInsured',
  'additionalInsured',
  'listOnly',
  'excludedPerson',
];

export const DriversList: FC<DriversListPropsType> = ({
  dataCompleteness,
  editingIdDisplay,
}) => {
  const { toast } = useToast();
  const {
    appId,
    config,
    refreshConfig,
    refreshRate,
    setDetails,
    details,
  } = useAppContext();
  const {
    resetForm,
    handleSubmit,
    submitForm,
    isSubmitting,
    values,
    status,
    setFieldValue,
  } = useFormikContext<FormValuesType>();

  const { editingId, changeEditingId } = useEdit();
  const hasFormChanges = useIsDirtyExcluding(['cfrIssueState']);

  const [{ loading: creatingDriver }, createDriver] = useLazyQuoteApi<
    CreateDriverResponseType
  >({
    method: 'POST',
    url: `/insurance_applications/${appId}/add_driver`,
  });

  const [
    { loading: creatingExcludedDriver },
    createExcludedDriver,
  ] = useLazyQuoteApi<CreateDriverResponseType>({
    method: 'POST',
    url: `/insurance_applications/${appId}/add_driver`,
    data: { excluded: true },
  });

  const [
    { loading: deletingDriver, error: deleteDriverError },
    deleteDriver,
  ] = useLazyQuoteApi<GetDetailsResponseType>({
    method: 'POST',
  });

  const executeCreateDriver = useCallback(
    async (excluded: boolean = false) => {
      try {
        const create = excluded ? createExcludedDriver : createDriver;
        const createDriverResponse = await create({
          transformResponse: transformCreateDriverResponse,
        });

        setDetails(createDriverResponse.data.appDetails);
        changeEditingId(createDriverResponse.data.id);
      } catch (e) {
        toast({
          type: 'error',
          message: getErrorMessage(e),
          withSecondaryNav: false,
          icon: 'error',
        });
      }
    },
    [setDetails, createDriver, createExcludedDriver, changeEditingId, toast]
  );

  const isAddDriverCommandAvailable = useMemo(
    () =>
      details.commands.insuranceProduct.some(
        ip => ip.name === 'add_driver' && ip.runnable
      ),
    [details.commands.insuranceProduct]
  );

  const isAddExcludedDriverCommandAvailable = useMemo(
    () =>
      details.commands.insuranceProduct.some(
        ip => ip.name === 'run_add_excluded_driver' && ip.runnable
      ),
    [details.commands.insuranceProduct]
  );

  const renderDriverStatusTag = (driverType: DriverType) => {
    if (driverType === 'additionalInsured' || driverType === 'principalInsured')
      return <></>;

    const getLabel = () => {
      switch (driverType) {
        case 'excludedPerson':
          return 'Excluded';
        case 'listOnly':
          return 'List-Only';
        default:
          return '';
      }
    };
    return (
      <Pill variant="disabled" className={styles.pill}>
        {getLabel()}
      </Pill>
    );
  };

  if (!config) {
    return <LoadingPage />;
  }

  const handleDeleteDriver = (
    driverId: string,
    data: any,
    driverLabel: string
  ) => {
    deleteDriver({
      url: `/insurance_applications/${appId}/drivers/${driverId}/delete`,
      transformResponse: transformAppDetails,
      data,
    })
      .then(response => {
        refreshConfig();
        refreshRate();
        changeEditingId(null);
        setDetails(response.data);
        toast({
          message: `${driverLabel} has been successfully deleted.`,
          type: 'success',
          withSecondaryNav: false,
        });
      })
      .catch(error => {
        toast({
          message: getErrorMessage(error),
          type: 'error',
        });
      });
  };

  return (
    <div data-testid="driver-list">
      <Accordion>
        {Object.keys(values.drivers)
          .filter(driverId => details.drivers[driverId]) // avoid race condition when deleting driver
          .sort((a: string, b: string) => {
            return (
              DRIVER_ORDER.indexOf(values.drivers[a].driverType) -
              DRIVER_ORDER.indexOf(values.drivers[b].driverType)
            );
          })
          .map((driverId: string, i) => {
            const driver = values.drivers[driverId];
            const driverLabel =
              driver.firstName === ''
                ? `Driver ${i + 1}` // make sure to display something if name isn't available (i.e on initial add)
                : formatDriverDisplay(driver, config);
            return (
              <AccordionItem
                key={driverId}
                editable={driver.editable}
                actions={setIsOpen => (
                  <>
                    <DriverStatusModal
                      driverId={driverId}
                      driverLabel={driverLabel}
                    />
                    <Modal
                      variant="tall"
                      toggle={openModal => (
                        <Button
                          data-testid={`remove-driver-${i + 1}`}
                          variant="danger-secondary"
                          size="sm"
                          disabled={
                            deletingDriver ||
                            isSubmitting ||
                            driver.driverType === 'principalInsured' ||
                            (editingId !== driverId && hasFormChanges)
                          }
                          onClick={() => {
                            if (config?.drivers?.bypassRemovalQuestions) {
                              handleDeleteDriver(driverId, {}, driverLabel);
                            } else {
                              openModal();
                            }
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    >
                      {close => (
                        <DriverRemovalModal
                          driverId={driverId}
                          values={values}
                          setFieldValue={setFieldValue}
                          isSubmittingUpdate={isSubmitting}
                          onSubmit={async (data, canBeRemoved) => {
                            if (canBeRemoved) {
                              handleDeleteDriver(driverId, data, driverLabel);
                            } else {
                              submitForm().then(() => {
                                refreshConfig();
                                refreshRate();
                                changeEditingId(null);
                              });
                            }
                          }}
                          close={close}
                          isSubmitting={deletingDriver}
                          error={deleteDriverError?.message}
                          driverLabel={driverLabel}
                        />
                      )}
                    </Modal>
                    {editingId === driverId ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        type="submit"
                        className={styles.saveAction}
                        disabled={deletingDriver || isSubmitting}
                        isSubmitting={isSubmitting}
                        onClick={() => handleSubmit()}
                        data-testid={`save-driver-${i + 1}-header`}
                      >
                        Save
                      </Button>
                    ) : hasFormChanges ? (
                      <Modal
                        variant="normal"
                        toggle={openModal => (
                          <Button
                            data-testid={`edit-driver-${i + 1}`}
                            className={styles.saveAction}
                            variant="secondary"
                            size="sm"
                            onClick={openModal}
                          >
                            Edit
                          </Button>
                        )}
                      >
                        {() => (
                          <SaveDiscardModal
                            onSave={async () => {
                              await submitForm();
                              setIsOpen(true);
                              changeEditingId(driverId);
                            }}
                            itemDisplay={editingIdDisplay}
                            saveSubmitting={isSubmitting}
                            onDiscard={() => {
                              resetForm();
                              changeEditingId(driverId);
                              setIsOpen(true);
                            }}
                          />
                        )}
                      </Modal>
                    ) : (
                      <Button
                        data-testid={`edit-driver-${i + 1}`}
                        className={styles.saveAction}
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          changeEditingId(driverId);
                          setIsOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </>
                )}
                startOpen={editingId === driverId}
                identifier={driverId}
                header={
                  <div className={styles.accordionHeaderContainer}>
                    <AccordionHeader
                      entity="driver"
                      header={driverLabel}
                      dataComplete={dataCompleteness[driverId]}
                    />
                    {driver.discoveredDriver && (
                      <Pill variant="info" className={styles.pill}>
                        Additional Driver
                      </Pill>
                    )}
                    {renderDriverStatusTag(driver.driverType)}
                  </div>
                }
                footer={
                  editingId === driverId && (
                    <>
                      <Button
                        data-testid={`cancel-edit-driver-${i + 1}`}
                        variant="secondary"
                        disabled={deletingDriver || isSubmitting}
                        onClick={() => {
                          changeEditingId(null);

                          if (status && status.newAppDetails) {
                            setDetails(status.newAppDetails);
                          } else {
                            resetForm();
                          }
                        }}
                        className={styles.cancelAction}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        isSubmitting={isSubmitting}
                        disabled={isSubmitting}
                        onClick={() => handleSubmit()}
                        data-testid={`save-driver-${i + 1}-footer`}
                      >
                        Save Driver
                      </Button>
                    </>
                  )
                }
              >
                <DriverForm
                  driverType={driver.driverType}
                  config={config.drivers}
                  driverId={driverId}
                />
              </AccordionItem>
            );
          })}
      </Accordion>

      <div className={styles.actions}>
        <div className={styles.addActions}>
          {hasFormChanges ? (
            <Modal
              variant="normal"
              toggle={openModal => (
                <Button
                  data-testid="add-driver"
                  variant="secondary"
                  disabled={
                    !isAddDriverCommandAvailable ||
                    creatingDriver ||
                    creatingExcludedDriver
                  }
                  isSubmitting={creatingDriver}
                  className={styles.primaryBtn}
                  onClick={() => openModal()}
                >
                  <Asset
                    icon={PlusIcon}
                    description="additional-driver-plus"
                    height={12}
                    width={12}
                  />
                  Add New Driver
                </Button>
              )}
            >
              {close => (
                <SaveDiscardModal
                  onSave={async () => {
                    await submitForm();
                    await executeCreateDriver();
                    close();
                  }}
                  itemDisplay={editingIdDisplay}
                  saveSubmitting={isSubmitting}
                  discardSubmitting={creatingDriver}
                  onDiscard={async () => {
                    await executeCreateDriver();
                    close();
                  }}
                />
              )}
            </Modal>
          ) : (
            <Button
              data-testid="add-driver"
              variant="secondary"
              disabled={
                !isAddDriverCommandAvailable ||
                creatingDriver ||
                creatingExcludedDriver
              }
              isSubmitting={creatingDriver}
              className={styles.primaryBtn}
              onClick={async () => {
                await executeCreateDriver();
              }}
            >
              <Asset
                icon={PlusIcon}
                description="additional-driver-plus"
                height={12}
                width={12}
              />
              Add New Driver
            </Button>
          )}
          {config.drivers.allowExcludedDrivers && (
            <>
              {hasFormChanges ? (
                <Modal
                  variant="normal"
                  toggle={openModal => (
                    <Button
                      data-testid="add-excluded-driver"
                      disabled={
                        !isAddExcludedDriverCommandAvailable ||
                        creatingDriver ||
                        creatingExcludedDriver
                      }
                      isSubmitting={creatingExcludedDriver}
                      variant="secondary"
                      className={styles.secondaryBtn}
                      onClick={() => openModal()}
                    >
                      <Asset
                        icon={PlusIcon}
                        description="additional-driver-plus"
                        height={12}
                        width={12}
                      />
                      Add Excluded Driver
                    </Button>
                  )}
                >
                  {close => (
                    <SaveDiscardModal
                      onSave={async () => {
                        await submitForm();
                        await executeCreateDriver(true); // when set to true, creates excluded driver
                        close();
                      }}
                      itemDisplay={editingIdDisplay}
                      saveSubmitting={isSubmitting}
                      discardSubmitting={creatingExcludedDriver}
                      onDiscard={async () => {
                        await executeCreateDriver(true);
                        close();
                      }}
                    />
                  )}
                </Modal>
              ) : (
                <Button
                  data-testid="add-excluded-driver"
                  variant="secondary"
                  className={styles.secondaryBtn}
                  disabled={
                    !isAddExcludedDriverCommandAvailable ||
                    creatingDriver ||
                    creatingExcludedDriver
                  }
                  isSubmitting={creatingExcludedDriver}
                  onClick={async () => {
                    await executeCreateDriver(true);
                  }}
                >
                  <Asset
                    icon={PlusIcon}
                    description="exclude-driver-plus"
                    height={12}
                    width={12}
                  />
                  Add Excluded Driver
                </Button>
              )}
            </>
          )}
          <div className={styles.disclosure}>
            <Asset
              icon={WarningIcon}
              description="Failed"
              height={16}
              width={16}
              className={styles.icon}
            />
            All drivers in household, or who may have access to a vehicle must
            be added.
          </div>
        </div>
      </div>
    </div>
  );
};
